/**
 * @file: LeadAffordanceButton.tsx
 *
 * Inline dark CTA shared by the maintenance-plan feed body and the
 * maintenance-plans timeline page: turn this plan detail into a lead
 * ("转为商机并跟进 →"), or open the lead that already owns it ("查看商机 →").
 *
 * Coverage is resolved by plan_detail_id. The feed body has no coverage in
 * hand, so it leaves `initialCoverage` undefined and the button fetches on
 * mount (hidden until the answer loads). The plans page already joins the
 * coverage into each row, so it passes `initialCoverage` and the button
 * renders immediately with no extra request.
 *
 * Converting is also the point where the reminders behind those details stop
 * being actionable, so the read-clearing write lives HERE rather than in the
 * feed thread — otherwise a conversion from the plans timeline would leave
 * the same reminders sitting unread in the inbox.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { useFeedUnreadStore } from '@euler/app/flows/feed/lib/unread/context';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadCoverageInfo } from '@euler/model/lead/types';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { LeadBundleSheet } from '../../LeadBundleSheet';
import { isCoveredByLead } from './leadCovered';

export const LeadAffordanceButton: FC<{
  planDetailId: number;
  /**
   * Pre-resolved coverage for this detail (the plans page joins it per row).
   * When set, the button skips the on-mount coverage fetch.
   */
  initialCoverage?: LeadCoverageInfo;
  /** Solid fill (default, feed) or outline (plans timeline). */
  variant?: 'solid' | 'outline';
  /** Called after a successful convert-to-lead with the full set of covered
   * plan details, so a mounted feed thread can flip every bundled reminder's
   * card to read without waiting for a refetch (the server write is ours). */
  onConverted?: (planDetailIds: number[]) => void;
  /** Surfaces the resolved coverage so the parent can gate the edit menu
   * (the feed has no coverage in hand otherwise). */
  onCoverageResolved?: (coverage: LeadCoverageInfo) => void;
}> = memo(
  ({
    planDetailId,
    initialCoverage,
    variant = 'solid',
    onConverted,
    onCoverageResolved,
  }) => {
    const { rpx } = useResponsiveContext();
    const { leadService, feedService } = useServiceFactory();
    const { refreshFeedUnread } = useFeedUnreadStore();
    const navigation = useAppNavigation();
    const [coverage, setCoverage] = useState<LeadCoverageInfo | undefined>(
      initialCoverage,
    );
    const [sheetShown, setSheetShown] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      // Coverage handed in by the caller — no lookup needed.
      if (initialCoverage != null) return;
      let active = true;
      void (async () => {
        try {
          const info = await leadService.coverageByPlanDetail(planDetailId);
          if (active) setCoverage(info);
        } catch (e) {
          console.error(e);
        }
      })();
      return () => {
        active = false;
      };
    }, [initialCoverage, leadService, planDetailId]);

    // Report coverage up (resolved or freshly created) for the parent's gate.
    useEffect(() => {
      if (coverage) onCoverageResolved?.(coverage);
    }, [coverage, onCoverageResolved]);

    // Open the lead detail as an app-level route (`_lead`) so it pushes onto
    // the ROOT stack — going back returns to this feed thread, not the lead
    // center.
    const openLeadDetail = useCallback(
      (leadId: string) => {
        (
          navigation as { navigate: (name: string, params: unknown) => void }
        ).navigate('_lead', { id: leadId });
      },
      [navigation],
    );

    // Open the bundle picker — the sheet fetches its own candidates so it can
    // re-query as the match window is adjusted.
    const onConvertPress = useCallback(() => setSheetShown(true), []);

    // The bundled details now belong to a lead, so their reminders are done —
    // clear them in the recipient's inbox (rows that aren't loaded here, or
    // aren't loaded at all on the plans page, included). Best-effort and
    // idempotent: a failure just leaves them unread until the reminder card
    // re-resolves its coverage and clears itself.
    const markRemindersRead = usePersistFn(async (planDetailIds: number[]) => {
      try {
        await feedService.markRemindersReadByPlanDetails(planDetailIds);
        await refreshFeedUnread();
      } catch (e) {
        console.error('markRemindersReadByPlanDetails failed', e);
      }
    });

    const onBundleConfirm = usePersistFn(async (planDetailIds: number[]) => {
      setSubmitting(true);
      try {
        const result = await leadService.create({ planDetailIds });
        setCoverage({
          coverage: 'covered',
          leadId: result.leadId,
          leadStatus: 'pending',
        });
        // Awaited before handing off: the caller reacts to `onConverted` by
        // refetching its list, which would otherwise race this write and read
        // the reminders back as unread. Errors are swallowed inside, so this
        // can't block the conversion — it only costs the sheet's spinner.
        await markRemindersRead(planDetailIds);
        setSheetShown(false);
        onConverted?.(planDetailIds);
        openLeadDetail(result.leadId);
      } catch (e) {
        console.error(e);
      } finally {
        setSubmitting(false);
      }
    });

    const onOpen = useCallback(() => {
      if (coverage?.leadId) openLeadDetail(coverage.leadId);
    }, [coverage, openLeadDetail]);

    if (coverage == null) return null;
    const isOpen = isCoveredByLead(coverage);
    const accent = isOpen ? '#1f80ff' : '#1f2937';
    const outline = variant === 'outline';
    return (
      <>
        <TouchableOpacity
          onPress={isOpen ? onOpen : onConvertPress}
          activeOpacity={0.7}
          css={`
            align-self: flex-start;
            margin-top: ${rpx(12)}px;
            margin-right: ${rpx(10)}px;
            padding: ${rpx(7)}px ${rpx(14)}px;
            border-radius: ${rpx(6)}px;
            border-width: 1px;
            border-color: ${accent};
            background-color: ${outline ? 'transparent' : accent};
          `}
        >
          <Typography.Text
            size={rpx(13)}
            color={outline ? accent : '#fff'}
            medium
          >
            {isOpen ? '查看商机 →' : '转为商机并跟进 →'}
          </Typography.Text>
        </TouchableOpacity>
        {!isOpen ? (
          <LeadBundleSheet
            show={sheetShown}
            planDetailId={planDetailId}
            submitting={submitting}
            onClose={() => setSheetShown(false)}
            onConfirm={ids => void onBundleConfirm(ids)}
          />
        ) : null}
      </>
    );
  },
);
